/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
/*
    xng-change:
    * Change the Embla carousel and use the swiper then clean all the unneeded code to make the file clean and readable 
 */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MonCarousel from "@monsma/Overrides/Components/Carousel";
import { screen } from "@monsma/XDL/Helpers";
import React, { ReactNode, useState } from "react";
import { css, ThemeProps } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
    products?: ProductModel[];
}

type Props = OwnProps & ThemeProps<BaseTheme>;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    carouselWrapper?: InjectableCss;
    attributeLabelWrapper?: InjectableCss;
    attributeLabelText?: TypographyPresentationProps;
    attributeValueWrapper?: InjectableCss;
    attributeValueText?: TypographyPresentationProps;
    bottomSectionWrapper?: InjectableCss;
}

export const carouselStyles: CarouselStyles = {
    carouselWrapper: {
        css: css`
            width: 100%;
            max-width: 100%;
            padding: 0 90px;
            position: relative;
            ${screen("gt", "lg")} {
                .swiper-button-prev {
                    left: -90px !important; // To override the carousel style
                }
                .swiper-button-next {
                    right: -90px !important;
                }
                .swiper-navigation > div {
                    position: absolute !important;
                    top: 50% !important;
                    &::after {
                        font-size: 35px !important;
                    }
                }
                .swiper-pagination-bullets {
                    display: none;
                }
                .swiper-container + .swiper-navigation {
                    margin-top: 0;
                }
            }
            ${screen("lte", "lg")} {
                padding: 0;
                .swiper-navigation {
                    margin-bottom: 40px;
                }
            }
            .swiper-slide > div {
                width: 100%;
            }
        `,
    },
    attributeLabelWrapper: {
        css: css`
            .swiper-container {
                border-left: 1px solid ${getColor("common.border")};
                border-right: 1px solid ${getColor("common.border")};
                background: ${getColor("common.background")};
                ${screen("gt", "xs")} {
                    .swiper-slide {
                        position: relative;

                        &:not(:first-child)::before {
                            content: " ";
                            position: absolute;
                            left: -30px;
                            height: 100%;
                            width: 1px;
                            border-right: 1px solid ${getColor("common.border")};
                        }
                    }
                }
            }
        `,
    },
    attributeLabelText: {
        variant: "h6",
        color: "text.inverse",
        css: css`
            padding: 10px 15px;
            background-color: ${getColor("secondary")};
            margin-bottom: 0;
        `,
    },
    attributeValueText: {
        lineHeight: "19px",
        css: css`
            padding: 10px 15px;
            display: block;
        `,
    },
    bottomSectionWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            margin-top: 40px;
        `,
    },
};

const PRODUCT_COMPARE_CAROUSEL_ID = "product-compare-carousel-id";

const Carousel = ({ id, extendedStyles, title, hideTitle, maxNumberOfColumns, slideDetails }: Props) => {
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    const getProductAttributeValue = (
        product: ProductModel,
        attribute: AttributeTypeModel,
        styles: CarouselStyles,
        id: string,
    ) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue[0].valueDisplay}
                </Typography>
            );
        }
        return <Typography {...styles.attributeValueText}>{translate("---")}</Typography>;
    };

    return (
        <StyledWrapper {...styles.carouselWrapper}>
            {slideDetails?.slides.length && (
                <>
                    <MonCarousel
                        id={PRODUCT_COMPARE_CAROUSEL_ID}
                        maxNumberOfColumns={3}
                        {...({ showArrows: true, showDots: true, slidesGap: 60, allowTouchMove: false } as any)}
                        slides={slideDetails.slides}
                        breakpoints={{
                            0: {
                                slidesPerView: 1,
                            },
                            600: {
                                slidesPerView: 2,
                            },
                            900: {
                                slidesPerView: 3,
                            },
                        }}
                    />
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        {slideDetails.attributeTypes.map(attribute => (
                            <StyledWrapper key={attribute.id} {...styles.attributeLabelWrapper}>
                                <Typography
                                    id={`carousel-${id}_attribute-label-${attribute.id}`}
                                    {...styles.attributeLabelText}
                                >
                                    {attribute.label || attribute.name}
                                </Typography>
                                <StyledWrapper>
                                    <MonCarousel
                                        id={PRODUCT_COMPARE_CAROUSEL_ID}
                                        maxNumberOfColumns={3}
                                        {...({ showArrows: false, showDots: false, slidesGap: 60, allowTouchMove: false } as any)}
                                        slides={slideDetails.products.map(product => ({
                                            id: product.id,
                                            renderComponent: function ProductCarouselProductCondensedRenderer() {
                                                return (
                                                    <StyledWrapper
                                                        key={`${product.id}-attribute-${attribute.id}`}
                                                        {...styles.attributeValueWrapper}
                                                    >
                                                        {getProductAttributeValue(product, attribute, styles, id)}
                                                    </StyledWrapper>
                                                );
                                            },
                                        }))}
                                        breakpoints={{
                                            0: {
                                                slidesPerView: 1,
                                            },
                                            600: {
                                                slidesPerView: 2,
                                            },
                                            900: {
                                                slidesPerView: 3,
                                            },
                                        }}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default Carousel;
